import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SIGNUP_ROLES } from "@/config/business";
import { SIGNUP_PACKAGES } from "@/config/supporter";

export const fieldCls = "bg-parchment border-line text-ink placeholder:text-ink-muted/60 rounded-xl focus-visible:ring-moss focus-visible:ring-offset-0";

const Field = ({ id, label, optional, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="font-bold text-ink">
      {label}
      {optional ? <span className="ml-1 font-semibold text-ink-muted">({optional})</span> : " *"}
    </Label>
    {children}
  </div>
);

const PackagePicker = ({ value, onChange }) => {
  const { t, fmtNum } = useLang();
  const s = t.pages.signup;
  const sp = t.pages.supporter;
  return (
    <fieldset>
      <legend className="font-bold text-ink mb-3">{s.package} *</legend>
      <div role="radiogroup" aria-label={s.package} className="grid sm:grid-cols-2 gap-3">
        {SIGNUP_PACKAGES.map((p) => {
          const on = value === p.key;
          const info = p.free ? s.host : sp.packages[p.key];
          return (
            <button
              type="button"
              role="radio"
              aria-checked={on}
              key={p.key}
              onClick={() => onChange(p.key)}
              data-testid={`signup-package-${p.key}`}
              className={`text-left rounded-2xl border-2 p-4 transition-colors ${p.free ? "sm:col-span-2 border-dashed" : ""} ${on ? "border-moss bg-moss/10" : "border-line bg-parchment hover:border-moss/50"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-display text-lg font-semibold text-ink">{info.name}</span>
                <span className="text-xs font-extrabold text-amber-dark whitespace-nowrap">{p.free ? s.placePin : p.trolls === 1 ? sp.troll : sp.trolls.replace("{n}", p.trolls)}</span>
              </div>
              <div className="mt-1 text-sm text-ink-muted">{info.for}</div>
              {p.free ? (
                <div className="mt-2 font-extrabold text-moss">{s.host.free}</div>
              ) : (
                <div className="mt-2 font-extrabold text-ink tabular-nums">
                  {fmtNum(p.launch)} kr <span className="ml-1 text-xs font-bold text-ink-muted line-through">{fmtNum(p.regular)} kr</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
};

export const SignupFields = ({ form, set, onRole }) => {
  const { t } = useLang();
  const s = t.pages.signup;
  const cats = t.pages.business.cats;
  const input = (k, type, auto, max) => (
    <Input id={`signup-${k}`} type={type} autoComplete={auto} maxLength={max} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`signup-${k}`} className={fieldCls} />
  );
  return (
    <div className="space-y-6">
      <Field id="signup-role" label={s.role}>
        <Select value={form.role} onValueChange={onRole}>
          <SelectTrigger id="signup-role" data-testid="signup-role" className={fieldCls}>
            <SelectValue placeholder={s.rolePh}>{form.role ? cats[form.role]?.name || s.roles[form.role] : undefined}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {SIGNUP_ROLES.map((r) => (
              <SelectItem key={r} value={r} data-testid={`signup-role-${r}`}>{cats[r]?.name || s.roles[r]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field id="signup-firstName" label={s.firstName}>{input("firstName", "text", "given-name", 80)}</Field>
        <Field id="signup-lastName" label={s.lastName}>{input("lastName", "text", "family-name", 80)}</Field>
        <Field id="signup-email" label={s.email}>{input("email", "email", "email", 254)}</Field>
        <Field id="signup-phone" label={s.phone}>{input("phone", "tel", "tel", 30)}</Field>
        <Field id="signup-company" label={s.company} optional={s.optional}>{input("company", "text", "organization", 120)}</Field>
        <Field id="signup-website" label={s.website} optional={s.optional}>{input("website", "url", "url", 200)}</Field>
      </div>
      <PackagePicker value={form.pkg} onChange={(v) => set("pkg", v)} />
    </div>
  );
};
